<template>
  <div class="pl-login">

    <!-- ===================== BRAND PANEL ===================== -->
    <div class="pl-brand pl-base-bg">
      <!-- Glows -->
      <div class="pl-glow pl-glow--a"></div>
      <div class="pl-glow pl-glow--b"></div>
      <div class="pl-glow pl-glow--c"></div>

      <!-- SVG grid -->
      <svg class="pl-grid" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="grid-signup" width="52" height="52" patternUnits="userSpaceOnUse">
            <path d="M 52 0 L 0 0 0 52" fill="none" stroke="rgba(255,255,255,0.22)" stroke-width="1"/>
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid-signup)" />
      </svg>

      <!-- Animated chart line at bottom -->
      <svg class="pl-chart" viewBox="0 0 600 400" preserveAspectRatio="none">
        <defs>
          <linearGradient id="plArea-signup" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stop-color="#bff5d6" stop-opacity="0.35" />
            <stop offset="1" stop-color="#bff5d6" stop-opacity="0" />
          </linearGradient>
        </defs>
        <path
          d="M0,330 L80,300 L160,318 L240,250 L320,268 L400,180 L480,150 L560,90 L600,70 L600,400 L0,400 Z"
          fill="url(#plArea-signup)" />
        <path class="pl-chart-line"
          d="M0,330 L80,300 L160,318 L240,250 L320,268 L400,180 L480,150 L560,90 L600,70"
          fill="none" stroke="rgba(215,251,230,0.85)" stroke-width="2.5"
          stroke-linecap="round" stroke-linejoin="round" />
        <circle cx="400" cy="180" r="5" fill="#fff" opacity="0.9"/>
        <circle cx="560" cy="90"  r="5.5" fill="#fff" opacity="0.9"/>
      </svg>

      <!-- Brand content -->
      <div class="pl-brand-content">
        <div class="pl-icon-tile">
          <div class="pl-icon-clip">
            <img src="@/assets/ProfitLedgex.png" alt="ProfitLedgex" class="pl-icon-img" />
          </div>
        </div>
        <h1 class="pl-wordmark">ProfitLedgex</h1>
        <p class="pl-tagline">Intelligent business insights with seamless invoicing.</p>
        <div class="pl-accent-bar"></div>
      </div>
    </div>

    <!-- ===================== FORM PANEL ===================== -->
    <div class="pl-form-panel">
      <div class="pl-form-glow"></div>
      <div class="pl-form-glow-2"></div>

      <div class="pl-form">
        <p class="pl-eyebrow">Get started</p>
        <h2 class="pl-heading">Create your account</h2>
        <p class="pl-sub">A verification code will be sent to your email.</p>

        <div class="pl-trial-note">
          <v-icon size="16" color="#0a8a6a">mdi-gift-outline</v-icon>
          Enjoy 15 days of free access. Your subscription payment is due after your trial ends.
        </div>

        <v-form ref="signupForm" v-model="isFormValid" lazy-validation @submit.prevent="handleSignup">
          <label class="pl-label">Full name</label>
          <v-text-field
            v-model.trim="name"
            placeholder="Your name"
            autocomplete="name"
            outlined
            hide-details="auto"
            :rules="nameRules"
            class="pl-field"
            color="#0a6cc0" />

          <label class="pl-label">Email address</label>
          <v-text-field
            v-model.trim="email"
            type="email"
            placeholder="you@company.com"
            autocomplete="email"
            outlined
            hide-details="auto"
            :rules="emailRules"
            class="pl-field"
            color="#0a6cc0" />

          <label class="pl-label">Phone number</label>
          <v-text-field
            v-model.trim="phone"
            type="tel"
            placeholder="10-digit mobile number"
            autocomplete="tel"
            outlined
            hide-details="auto"
            :rules="phoneRules"
            class="pl-field"
            color="#0a6cc0" />

          <label class="pl-label">Username (optional)</label>
          <v-text-field
            v-model.trim="username"
            placeholder="Choose a username to log in with"
            autocomplete="username"
            outlined
            hide-details="auto"
            :rules="usernameRules"
            class="pl-field"
            color="#0a6cc0"
            @input="username = username.toLowerCase()" />

          <label class="pl-label">Password</label>
          <v-text-field
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            placeholder="At least 6 characters"
            autocomplete="new-password"
            outlined
            hide-details="auto"
            :rules="passwordRules"
            class="pl-field"
            color="#0a6cc0">
            <template #append>
              <v-icon
                tabindex="0"
                role="button"
                :aria-label="showPassword ? 'Hide password' : 'Show password'"
                style="cursor: pointer;"
                @click="showPassword = !showPassword"
                @keydown.enter="showPassword = !showPassword">
                {{ showPassword ? 'mdi-eye-off' : 'mdi-eye' }}
              </v-icon>
            </template>
          </v-text-field>

          <v-checkbox
            v-model="acceptedTerms"
            :rules="termsRules"
            hide-details="auto"
            class="pl-terms-check mt-4"
            color="#0a6cc0">
            <template #label>
              <span class="pl-terms-label">
                I agree to the
                <router-link to="/terms" target="_blank" @click.native.stop>Terms &amp; Conditions</router-link>
                and
                <router-link to="/privacy" target="_blank" @click.native.stop>Privacy Policy</router-link>
              </span>
            </template>
          </v-checkbox>

          <v-alert v-if="errorMessage" type="error" dense outlined class="mt-3 mb-0">
            {{ errorMessage }}
          </v-alert>

          <v-btn
            type="submit"
            block
            depressed
            height="54"
            :loading="loading"
            :disabled="!isFormValid || !acceptedTerms || loading"
            class="pl-submit mt-5">
            Create account
          </v-btn>
        </v-form>

        <div class="pl-secure">
          <v-icon size="14" color="#a0adb8">mdi-lock-outline</v-icon>
          Secured with 256-bit SSL encryption
        </div>

        <p class="pl-switch">
          Already have an account?
          <router-link to="/login" class="pl-switch-link">Sign in</router-link>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { TERMS_VERSION } from '@/utils/legal';

export default {
  name: 'SignupPage',

  data: () => ({
    name: '',
    email: '',
    phone: '',
    username: '',
    password: '',
    acceptedTerms: false,
    showPassword: false,
    isFormValid: false,
    loading: false,
    errorMessage: '',

    nameRules: [
      (v) => !!v || 'Name is required',
    ],
    emailRules: [
      (v) => !!v || 'Email is required',
      (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || 'Enter a valid email address',
    ],
    phoneRules: [
      (v) => !!v || 'Phone number is required',
      (v) => /^\d{10}$/.test(v) || 'Enter a valid 10-digit phone number',
    ],
    usernameRules: [
      (v) => !v || /^[a-z0-9_.]{4,20}$/.test(v) ||
        '4-20 chars: lowercase letters, numbers, underscore, dot',
    ],
    passwordRules: [
      (v) => !!v || 'Password is required',
      (v) => v.length >= 6 || 'Password must be at least 6 characters',
    ],
    termsRules: [
      (v) => !!v || 'You must agree to the Terms & Conditions and Privacy Policy',
    ],
  }),

  methods: {
    async handleSignup() {
      this.errorMessage = '';
      if (!this.$refs.signupForm.validate()) return;
      this.loading = true;
      try {
        const result = await this.$store.dispatch('auth/register', {
          name: this.name,
          email: this.email.toLowerCase(),
          phone: this.phone,
          username: this.username || undefined,
          password: this.password,
          termsAccepted: this.acceptedTerms,
          termsVersion: TERMS_VERSION,
        });
        this.$router.push({
          path: '/verify-otp',
          query: { userId: result.userId, email: this.email.toLowerCase() },
        });
      } catch (error) {
        this.errorMessage = error.response?.data?.message || 'Failed to create account';
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ── Layout (shared with login.vue's brand-panel visual system) ── */
.pl-login {
  display: flex;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  font-family: 'Manrope', system-ui, sans-serif;
  color: #0e2433;
}

/* ════════════════════════════
   LEFT — Brand Panel
════════════════════════════ */
.pl-brand {
  position: relative;
  flex: 0 0 54%;
  overflow: hidden;
}

.pl-glow { position: absolute; border-radius: 50%; pointer-events: none; }

.pl-glow--a {
  top: -90px; left: -90px;
  width: 440px; height: 440px;
  background: radial-gradient(circle,
    rgba(190,235,255,0.95) 0%,
    rgba(110,195,255,0.60) 32%,
    rgba(60,140,255,0.18) 58%,
    transparent 72%);
  filter: blur(38px);
  animation: pl-floatA 18s ease-in-out infinite;
}
.pl-glow--b {
  bottom: -160px; right: -80px;
  width: 480px; height: 480px;
  background: radial-gradient(circle, rgba(50,220,130,0.28), transparent 68%);
  filter: blur(90px);
  animation: pl-floatB 22s ease-in-out infinite;
}
.pl-glow--c {
  top: 38%; right: 12%;
  width: 220px; height: 220px;
  background: radial-gradient(circle, rgba(255,255,255,0.12), transparent 70%);
  filter: blur(80px);
  animation: pl-floatC 14s ease-in-out infinite;
}

.pl-grid {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  z-index: 1; pointer-events: none;
}

.pl-chart {
  position: absolute; left: 0; bottom: 0;
  width: 100%; height: 58%;
  opacity: 0.55; z-index: 2;
}
.pl-chart-line {
  stroke-dasharray: 1400;
  animation: pl-draw 2.8s cubic-bezier(.4,0,.2,1) forwards;
}

.pl-brand-content {
  position: absolute; inset: 0; z-index: 3;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  padding: 48px; text-align: center;
  animation: pl-rise 0.8s ease-out both;
}

.pl-icon-tile {
  padding: 16px;
  border-radius: 36px;
  background: rgba(255,255,255,0.12);
  box-shadow:
    0 28px 64px rgba(2,20,40,0.50),
    inset 0 0 0 1.5px rgba(255,255,255,0.22),
    inset 0 2px 4px rgba(255,255,255,0.15);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.pl-icon-clip {
  width: 128px;
  height: 128px;
  overflow: hidden;
  border-radius: 26px;
  box-shadow: 0 8px 24px rgba(2,20,40,0.40);
  position: relative;
}
.pl-icon-img {
  display: block;
  position: absolute;
  top: 0; left: 0;
  width: 128px;
  height: auto;
}

.pl-wordmark {
  margin: 28px 0 0;
  font-weight: 800;
  font-size: 46px;
  letter-spacing: -0.025em;
  color: #fff;
  text-shadow:
    0 2px 12px rgba(2,20,40,0.35),
    0 0 60px rgba(120,210,255,0.25);
}

.pl-tagline {
  margin: 12px 0 0;
  font-size: 16px;
  font-weight: 500;
  color: rgba(225,245,255,0.88);
  letter-spacing: 0.01em;
}

.pl-accent-bar {
  margin-top: 28px;
  height: 3px; width: 56px;
  border-radius: 3px;
  background: linear-gradient(90deg, #7fd4ff, #74f0a8);
  box-shadow: 0 0 12px rgba(127,212,255,0.6);
}

/* ════════════════════════════
   RIGHT — Form Panel
════════════════════════════ */
.pl-form-panel {
  position: relative;
  flex: 1 1 46%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 48px;
  background: #f4f6f9;
  overflow-y: auto;
}

.pl-form-glow {
  position: absolute; top: -60px; right: -60px;
  width: 340px; height: 340px;
  background: radial-gradient(circle at 60% 40%, rgba(15,138,106,0.09), transparent 68%);
  pointer-events: none;
}
.pl-form-glow-2 {
  position: absolute; bottom: -80px; left: -40px;
  width: 280px; height: 280px;
  background: radial-gradient(circle, rgba(10,108,192,0.07), transparent 68%);
  pointer-events: none;
}

.pl-form {
  position: relative;
  width: 100%;
  max-width: 400px;
  padding: 32px 0;
  animation: pl-rise 0.8s ease-out 0.08s both;
}

.pl-eyebrow {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #0a8a6a;
}

.pl-heading {
  margin: 10px 0 6px;
  font-weight: 800;
  font-size: 30px;
  letter-spacing: -0.025em;
  color: #0b1e2d;
  line-height: 1.2;
}

.pl-sub {
  margin: 0 0 26px;
  font-size: 14.5px;
  color: #64747f;
  font-weight: 400;
}

.pl-trial-note {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: -12px 0 20px;
  padding: 10px 14px;
  border-radius: 10px;
  background: #e9f7f1;
  border: 1px solid #cdece0;
  color: #0a5940;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.4;
}

.pl-label {
  display: block;
  margin: 16px 0 6px;
  font-size: 13px;
  font-weight: 600;
  color: #374955;
  letter-spacing: 0.01em;
}

.pl-field >>> .v-input__slot {
  border-radius: 12px !important;
  background: #ffffff !important;
  min-height: 50px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06) !important;
}
.pl-field >>> fieldset {
  border-color: #cdd5dc !important;
  border-width: 1.5px !important;
}
.pl-field >>> .v-input--is-focused fieldset {
  border-color: #0a6cc0 !important;
  border-width: 2px !important;
}
.pl-field >>> input {
  font-size: 14.5px;
  color: #0b1e2d;
}
.pl-field >>> input::placeholder { color: #a8b5bf; }

.pl-submit {
  border-radius: 12px !important;
  background: linear-gradient(135deg, #0a5fb5 0%, #0d8f75 52%, #16b358 100%) !important;
  color: #fff !important;
  font-weight: 700 !important;
  letter-spacing: 0.02em !important;
  font-size: 16px !important;
  text-transform: none !important;
  box-shadow:
    0 8px 24px rgba(10,108,192,0.30),
    0 4px 8px rgba(13,143,117,0.20) !important;
  transition: opacity 0.2s, box-shadow 0.2s !important;
}
.pl-submit:hover {
  box-shadow:
    0 12px 32px rgba(10,108,192,0.38),
    0 6px 12px rgba(13,143,117,0.28) !important;
}

.pl-terms-check >>> .v-label {
  font-size: 13px;
  color: #374955;
}
.pl-terms-label a {
  color: #0a6cc0;
  font-weight: 700;
  text-decoration: none;
}
.pl-terms-label a:hover { text-decoration: underline; }

.pl-secure {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  margin-top: 22px;
  color: #9daab5;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.01em;
}

.pl-switch {
  margin-top: 18px;
  text-align: center;
  font-size: 13.5px;
  color: #64747f;
}

.pl-switch-link {
  color: #0a6cc0;
  font-weight: 700;
  text-decoration: none;
}
.pl-switch-link:hover {
  text-decoration: underline;
}

/* ── Animations ── */
@keyframes pl-floatA {
  0%,100% { transform: translate(0, 0); }
  50%      { transform: translate(30px, -42px); }
}
@keyframes pl-floatB {
  0%,100% { transform: translate(0, 0); }
  50%      { transform: translate(-28px, 36px); }
}
@keyframes pl-floatC {
  0%,100% { transform: translate(0, 0); }
  50%      { transform: translate(18px, 26px); }
}
@keyframes pl-rise {
  from { opacity: 0; transform: translateY(18px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes pl-draw {
  from { stroke-dashoffset: 1400; }
  to   { stroke-dashoffset: 0; }
}

/* ── Mobile ── */
@media (max-width: 860px) {
  .pl-login {
    flex-direction: column;
    height: auto;
    min-height: 100vh;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .pl-brand {
    flex: 0 0 auto;
    height: 220px;
    min-height: unset;
  }

  .pl-brand-content {
    padding: 24px 20px;
  }

  .pl-icon-tile {
    padding: 10px;
    border-radius: 22px;
  }

  .pl-icon-clip {
    width: 76px;
    height: 76px;
    border-radius: 16px;
  }

  /* Same uncropped fill as desktop — see login.vue for why this changed
     from a hand-tuned crop to a plain scale-to-fit. */
  .pl-icon-img {
    width: 76px;
    height: auto;
    top: 0;
    left: 0;
  }

  .pl-wordmark {
    font-size: 26px;
    margin: 12px 0 0;
  }

  .pl-tagline {
    font-size: 13px;
    margin: 6px 0 0;
    white-space: normal;
  }

  .pl-accent-bar {
    margin-top: 12px;
    width: 36px;
  }

  .pl-glow--a {
    width: 240px; height: 240px;
    top: -50px; left: -50px;
    filter: blur(28px);
  }

  .pl-form-panel {
    flex: 1;
    min-height: unset;
    align-items: flex-start;
    padding: 36px 24px 52px;
  }

  .pl-form { max-width: 100%; padding: 0; }

  .pl-heading { font-size: 24px; }

  .pl-sub {
    font-size: 14px;
    margin-bottom: 18px;
  }
}
</style>
